import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Icon, Tag } from "@nubeteck/components";
import {
  CardCalendarSubjectsStyled,
  ErrorTableMessage,
  SubjectButtonStyled,
  SubjectsContainerStyled,
  SubjectsListStyled,
  TabContainerStyled,
} from "./register-selection.styled";
import { type TabsProps, Button, Card, Modal, Skeleton } from "antd";
import {
  SubjectCalendarSchedule,
  SubjectDescriptionDisplay,
  SubjectTableSchedule,
} from "../../registration";
import ButtonSubject from "./button-subject";
import { ISection, ISelectedSubject, ISubject } from "src/Interfaces/subject";
import { horas } from "../../../constants";
import { useAppDispatch, useAppSelector } from "src/core/store";
import { setSubjectCodeForChange } from "src/core/store/slices/subjectSelectionSlice";
import useManageSubjects from "./hooks/useManageSubjects";
import NoSubjectsText from "./noSubjectsText";
import { FormatterUtils } from "@nubeteck/utils";
import { useLocale } from "../../../i18n/localeContext";
import { getUserEnrollementPage } from "../../../Api/content";
import { StrapiError } from "../../../pages/StrapiError";
import { _translateValue } from "../../../pages/admin-pages/translation-gestor/translateValue";
import { formatStrapiTemplate } from "../../../utils/strapi-template";

const SCREEN_NAME = "PantallaRegisterSelection";
import { useGetGeneralSettingByIdMutation } from "../../../services/generalSettingsServices";
import { useGetCursosReprobadosMutation } from "../../../services/subjectsServices";
import { useEsEstudiantePatrocinadoMutation } from "../../../services/sponsorshipsServices";

// const SCREEN_NAME = "PantallaRegisterSelection";

const RegisterSelection = ({
  subjectsData,
  isLoading,
  updateSubjects,
  selectedData,
  registeredSubjects,
  periodId,
  setSufficiencyDrawerVisible,
}: {
  subjectsData: ISubject[];
  isLoading: boolean;
  updateSubjects: () => void;
  selectedData: any[];
  registeredSubjects: any[];
  periodId: number;
  setSufficiencyDrawerVisible: () => void;
}) => {
  const { setValue, getValues } = useFormContext();
  const dispatch: any = useAppDispatch();
  const { subjectCodeForChange } = useAppSelector((state: any) => state.subjectSelection);
  const [modal, contextHolder] = Modal.useModal();
  const [selectedSubject, setSelectedSubject] = React.useState<ISelectedSubject | undefined>();
  const [subjects, setSubjects] = React.useState<ISubject[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
    const { locale } = useLocale();
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [translatedData, setTranslatedData] = React.useState<{
    asignaturas: { [key: string]: string };
  }>({
    asignaturas: {},
  });
  const [mensajeAsignaturaReprobada, setMensajeAsignaturaReprobada] = useState<string>("");
  const [getGeneralSettingById] = useGetGeneralSettingByIdMutation();
  const [cursosReprobadosCodes, setCursosReprobadosCodes] = useState<string[]>([]);
  const [getCursosReprobados] = useGetCursosReprobadosMutation();
  const { userInfo } = useAppSelector((state: any) => state.user);
  const [tienePatrocinioAmazon, setTienePatrocinioAmazon] = useState<boolean>(false);
  const [esEstudiantePatrocinado] = useEsEstudiantePatrocinadoMutation();

  // Cargar mensaje de asignatura reprobada desde configuración general
  useEffect(() => {
    getGeneralSettingById("MensajeAsignaturaReprobada")
      .unwrap()
      .then((res: any) => {
        if (res?.responseStatus === "Success" && res?.data) {
          setMensajeAsignaturaReprobada(res.data);
        }
      })
      .catch(() => {
        // En caso de error, mantener el mensaje vacío
      });
  }, [getGeneralSettingById]);

  // Cargar lista de cursos reprobados del estudiante
  useEffect(() => {
    const carreraId = getValues()?.programa?.carreraId;
    const matricula = userInfo?.id;

    if (matricula && carreraId) {
      getCursosReprobados({ matricula, carreraId })
        .unwrap()
        .then((res: any) => {
          if (res?.responseStatus === "Success" && res?.data?.code) {
            setCursosReprobadosCodes(res.data.code);
          }
        })
        .catch(() => {
          setCursosReprobadosCodes([]);
        });
    }
  }, [getCursosReprobados, userInfo?.id, getValues]);

  // Verificar si el estudiante tiene patrocinio Amazon (IDs 1 y 2)
  useEffect(() => {
    const estudianteId = userInfo?.estudianteId;

    if (estudianteId) {
      // Verificar patrocinio Amazon Colones (ID 1) y Amazon Dolares (ID 2)
      Promise.all([
        esEstudiantePatrocinado({ patrocinioId: 1, estudianteId }).unwrap(),
        esEstudiantePatrocinado({ patrocinioId: 2, estudianteId }).unwrap(),
      ])
        .then(([resColones, resDolares]: any[]) => {
          // Manejar ambos formatos: respuesta directa (true/false) o con estructura { data: true/false }
          const tieneColones = resColones === true || resColones?.data === true;
          const tieneDolares = resDolares === true || resDolares?.data === true;
          setTienePatrocinioAmazon(tieneColones || tieneDolares);
        })
        .catch(() => {
          setTienePatrocinioAmazon(false);
        });
    }
  }, [esEstudiantePatrocinado, userInfo?.estudianteId]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserEnrollementPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const esMatriculaAdministrativa = localStorage.getItem("matriculaAdministrativa") === "true";
  React.useEffect(() => {
    setSubjects(subjectsData);

    if (selectedSubject) {
      const subSelected = subjectsData?.find(
        (sub) => sub.asignaturaId == selectedSubject?.asignaturaId,
      );
      setSelectedSubject({
        ...selectedSubject,
        secciones: subSelected?.secciones?.map((sec: ISection) => ({
          ...sec,
          eventClass: subSelected?.eventClass,
          discipline: subSelected.discipline,
        })) as ISection[],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectsData]);

  React.useEffect(() => {
    const subjectForChange = subjects?.find((sub) => sub.asignaturaCodigo === subjectCodeForChange);
    if (subjectForChange)
      setSelectedSubject({
        ...subjectForChange,
        seccionId: 0,
        horarios: [],
        secciones: subjectForChange.secciones as ISection[],
      });
  }, [subjectCodeForChange, subjects]);

  React.useEffect(() => {
    return () => {
      dispatch(setSubjectCodeForChange(""));
    };
  }, [dispatch]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      const hasData = subjects && Array.isArray(subjects) && subjects.length > 0;
      if (!hasData) {
        setTranslatedData({ asignaturas: {} });
        return;
      }

      const asignaturasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      subjects.forEach((subject: ISubject) => {
        if (subject.asignaturaNombre && !asignaturasTranslations.hasOwnProperty(subject.asignaturaNombre)) {
          asignaturasTranslations[subject.asignaturaNombre] = null;
          translationPromises.push(
            _translateValue(SCREEN_NAME, subject.asignaturaNombre, locale).then((translated) => {
              asignaturasTranslations[subject.asignaturaNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);
      setTranslatedData({
        asignaturas: asignaturasTranslations as { [key: string]: string },
      });
    };
    translateData();
  }, [subjects, locale]);

  const showElectivasMessage = () => {
    modal.info({
      title:
        userEnrollementPage?.couse_selection?.elective_not_allowed_title ||
        "No es posible seleccionar esta electiva",
      icon: <Icon name="" color="#FFC53D" />,
      content:
        userEnrollementPage?.couse_selection?.elective_not_allowed_content ||
        "Ya ha seleccionado una electiva de la misma disciplina.",
      okText: userEnrollementPage?.ok_button || "Aceptar",
      style: { top: 200 },
    });
  };

  const confirm = (newSection: ISection, actualSection: ISelectedSubject) => {
    const subjectName =
      translatedData.asignaturas[actualSection?.asignaturaNombre ?? ""] ||
      actualSection?.asignaturaNombre;
    const selectedSubjectName =
      translatedData.asignaturas[selectedSubject?.asignaturaNombre ?? ""] ||
      selectedSubject?.asignaturaNombre;

    if (actualSection?.estaInscrita) {
      modal.info({
        title:
          userEnrollementPage?.couse_selection?.schedule_conflict_registered_title ||
          "No es posible matricular en este horario",
        icon: <Icon name="" color="#FFC53D" />,
        content: formatStrapiTemplate(
          userEnrollementPage?.couse_selection?.schedule_conflict_registered_content,
          `Ya ha matriculado la asignatura ${subjectName}, que coincide con este horario.`,
          { subjectName: subjectName ?? "" },
        ),
        okText: userEnrollementPage?.ok_button || "Aceptar",
        style: { top: 200 },
      });
    } else {
      modal.confirm({
        title: formatStrapiTemplate(
          userEnrollementPage?.couse_selection?.add_section_confirm_title,
          `¿Desea agregar la sección de ${selectedSubjectName} en este horario?`,
          { subjectName: selectedSubjectName ?? "" },
        ),
        icon: <Icon name="" color="#FFC53D" />,
        content: formatStrapiTemplate(
          userEnrollementPage?.couse_selection?.add_section_confirm_content,
          `La sección ${newSection.asignaturaSeccionCodigo} anteriormente escogida de ${subjectName} será removida.`,
          {
            sectionCode: newSection.asignaturaSeccionCodigo,
            subjectName: subjectName ?? "",
          },
        ),
        okText: userEnrollementPage?.ok_button || "Aceptar",
        onOk: () => {
          addSection(newSection);
        },
        cancelText: userEnrollementPage?.cancel_button || "Cancelar",
        style: { top: 200 },
      });
    }
  };

  const {
    getSlotsBySection,
    findScheduleCoincidence,
    removeSection,
    handleCollision,
    addSection,
    verifyCollision,
    getAvailableSlots,
    isLoadingSomething,
    selectedSections,
    verificarElectivasIguales,
  } = useManageSubjects(
    selectedData,
    registeredSubjects,
    subjects,
    selectedSubject,
    confirm,
    updateSubjects,
    periodId,
  );

  React.useEffect(() => {
    setValue("materiasSeleccionadas", selectedSections);
  }, [selectedSections, setValue]);

  const getSelectedSectionsAndCollisions = (startHour: { twentyFour: string; twelve: string }) => {
    const sectionsWithButtons = findScheduleCoincidence(startHour, selectedSections)?.map(
      ({ seccion, horarios }) => {
        const button = (
          <ButtonSubject
            randomColor={seccion.color}
            section={seccion.asignaturaNombre}
            type="selected"
            handleChange={
              selectedSections?.find((sect) => sect.seccionId === seccion.seccionId)
                ? () => {
                    removeSection(seccion);
                  }
                : verificarElectivasIguales(seccion)
                ? () => showElectivasMessage()
                : () => handleCollision(verifyCollision(seccion))
            }
          />
        );

        return { ...seccion, horarios, button };
      },
    );

    const sectionsToAdd = getSectionActiveByHour(startHour).filter(
      (sec) =>
        !sectionsWithButtons?.find(
          (s) =>
            s.seccionId === sec.seccionId &&
            s.horarios[0].diaId === sec.horarios[0].diaId &&
            s.horarios[0].horaInicio === sec.horarios[0].horaInicio &&
            s.horarios[0].horaFin === sec.horarios[0].horaFin,
        ),
    );

    const alreadyRegisteredSections = getAlreadyRegisteredSectionsByHour(startHour).filter(
      (sec) =>
        !sectionsWithButtons?.find(
          (s) =>
            s.seccionId === sec.seccionId &&
            s.horarios[0].diaId === sec.horarios[0].diaId &&
            s.horarios[0].horaInicio === sec.horarios[0].horaInicio &&
            s.horarios[0].horaFin === sec.horarios[0].horaFin,
        ),
    );

    return [...sectionsWithButtons, ...alreadyRegisteredSections, ...sectionsToAdd];
  };

  const getAlreadyRegisteredSectionsByHour = (startHour: {
    twentyFour: string;
    twelve: string;
  }) => {
    const sectionsWithButtons = findScheduleCoincidence(startHour, registeredSubjects ?? [])?.map(
      ({ seccion, horarios }) => {
        const button = (
          <ButtonSubject
            type="registered"
            randomColor="rgba(206, 161, 0, "
            section={seccion.asignaturaNombre}
            status="Inscrita"
            handleChange={() => null}
          />
        );

        return { ...seccion, horarios, button };
      },
    );
    return sectionsWithButtons;
  };

  const getSectionActiveByHour = (startHour: { twentyFour: string; twelve: string }) => {
    const sectionsWithButtons = findScheduleCoincidence(
      startHour,
      selectedSubject?.secciones ?? [],
    )?.map(({ seccion, horarios }) => {
      const collisionResult = verifyCollision(seccion);
      const isCollision = collisionResult.subjectCollision;

      const button = (
        <ButtonSubject
          type={isCollision ? "collision" : "avaliable"}
          randomColor="rgba(206, 161, 0, "
          section={`sección ${seccion.asignaturaSeccionCodigo}`}
          status={getSlotsBySection(seccion.seccionId)}
          handleChange={() => handleCollision(collisionResult)}
        />
      );

      return { ...seccion, horarios, button };
    });
    return sectionsWithButtons;
  };

  const getSesion = (monedaId: number) => {
    return monedaId === 2 ? "₡" : "$";
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.ofert_sb || "Oferta"),
      children: (
        <SubjectTableSchedule
          isLoading={isLoadingSomething || isLoading}
          secciones={selectedSubject?.secciones?.map((sc) => {
            const cupo: string = sc.cupo;
            // const cupo: string = getSlotsBySection(sc.seccionId);
            const estaSeleccionada = selectedSections?.find(
              (sect) => sect.seccionId === sc.seccionId,
            );
            return {
              ...sc,
              cupo,
              seccionPrecio: `${getSesion(selectedSubject.monedaId ?? 2)} ${FormatterUtils.number(
                (sc.seccionPrecio as number)?.toFixed(2),
              )}`,
              acciones: (
                <Button
                  type="link"
                  disabled={
                    isLoadingSomething ||
                    ((cupo === "lleno" || cupo == "0" || cupo === "Indefinido") &&
                      !estaSeleccionada)
                  }
                  onClick={
                    estaSeleccionada
                      ? () => {
                          removeSection(sc);
                        }
                      : verificarElectivasIguales(sc)
                      ? () => showElectivasMessage()
                      : () => {
                          handleCollision(verifyCollision(sc));
                        }
                  }
                >
                  {estaSeleccionada ? "Remover selección" : "Seleccionar"}
                  {verifyCollision(sc).subjectCollision !== undefined ? (
                    <ErrorTableMessage>Choque de horarios</ErrorTableMessage>
                  ) : null}
                </Button>
              ),
            };
          })}
        />
      ),
    },
    {
      key: "2",
      label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.view_calendar_ofert_sb || "Oferta en vista de calendario"),
      children: (
        <SubjectCalendarSchedule
          isLoading={isLoadingSomething || isLoading}
          secciones={horas.map((hr) => ({
            hora: hr.twelve,
            secciones: getSelectedSectionsAndCollisions(hr)
              .sort((a, b) => a.seccionId - b.seccionId)
              ?.map((sc) => ({
                ...sc,
                // cupo: getSlotsBySection(sc.seccionId),
              })),
          }))}
        />
      ),
    },
    {
      key: "3",
      label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.subject_desc_sb || "Descripción de la materia"),
      children: (
        <SubjectDescriptionDisplay
          descripcion={selectedSubject?.descripcion ?? ""}
          asignaturaNombre={
            selectedSubject?.asignaturaNombre ??
            (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.subject_desc_message || "Selecciona una materia para ver su información y horario."))
          }
          contenido={selectedSubject?.contenido ?? []}
          asignaturaCodigo={selectedSubject?.asignaturaCodigo ?? ""}
          asignaturaId={0}
        />
      ),
    },
  ];

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserEnrollementPage(null);
      }}
    />;
  }
  return (
    <Box style={{ position: "relative" }}>
      {subjects?.length == 0 ? (
        <>
          {esMatriculaAdministrativa && (
            <Button
              style={{
                position: "absolute",
                top: "20px",
                right: "20px",
                zIndex: 20,
                borderColor: "#006aea",
                color: "#006aea",
              }}
              type="default"
              onClick={setSufficiencyDrawerVisible}
            >
              Agregar suficiencia
            </Button>
          )}
          <NoSubjectsText />
        </>
      ) : (
        <CardCalendarSubjectsStyled>
          <Box>
            <SubjectsListStyled>
              <SubjectsContainerStyled>
                {esMatriculaAdministrativa && (
                  <Button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderColor: "#006aea",
                      color: "#006aea",
                    }}
                    type="default"
                    onClick={setSufficiencyDrawerVisible}
                  >
                    <Icon name="" />
                    Suficiencia
                  </Button>
                )}
                {isLoading ? (
                  <>
                    <Skeleton loading={true} active round />
                    <Skeleton loading={true} active round />
                    <Skeleton loading={true} active round />
                  </>
                ) : (
                  subjects?.map((subject) => (
                    <SubjectButtonStyled
                      key={subject.asignaturaCodigo}
                      style={{ alignItems: "start" }}
                      type="text"
                      onClick={() => {
                        getAvailableSlots(
                          subject.asignaturaId ?? 0,
                          getValues().programa.carreraId,
                        ).then(() => {
                          setSelectedSubject({
                            ...subject,
                            seccionId: 0,
                            horarios: [],
                            secciones: subject.secciones?.map((sec) => ({
                              ...sec,
                              eventClass: subject.eventClass,
                              discipline: subject.discipline,
                            })) as ISection[],
                          });
                        });
                      }}
                      className={
                        selectedSubject?.asignaturaCodigo === subject.asignaturaCodigo
                          ? "is-selected"
                          : ""
                      }
                    >
                      <span>
                        {subject.asignaturaCodigo} {translatedData.asignaturas[subject.asignaturaNombre] || subject.asignaturaNombre}
                      </span>
                      <Box>Créditos: {subject.creditos}</Box>
                      <Box>H.S: {subject.hs}</Box>
                      {subject.esSuficiencia && (
                        <span className="cost-container">
                          <Tag color="processing" bordered={false}>
                            Suficiencia
                          </Tag>
                        </span>
                      )}
                      <hr />
                    </SubjectButtonStyled>
                  ))
                )}
              </SubjectsContainerStyled>
            </SubjectsListStyled>
            <Box>
              {selectedSubject?.asignaturaNombre !== selectedSubject?.descripcion ? (
                <Card style={{ border: "1px solid rgb(190, 30, 100)", marginTop: "20px" }}>
                  {selectedSubject?.descripcion}
                </Card>
              ) : null}
              {selectedSubject &&
                mensajeAsignaturaReprobada &&
                tienePatrocinioAmazon &&
                cursosReprobadosCodes.includes(selectedSubject.asignaturaCodigo ?? "") && (
                  <Card
                    style={{
                      border: "1px solid rgb(190, 30, 100)",
                      marginTop: "20px",
                      backgroundColor: "rgba(190, 30, 100, 0.05)",
                    }}
                  >
                    {mensajeAsignaturaReprobada}
                  </Card>
                )}
              <TabContainerStyled
                defaultActiveKey={subjectCodeForChange === "" ? "1" : "2"}
                items={items}
              />
            </Box>
          </Box>
        </CardCalendarSubjectsStyled>
      )}
      {contextHolder}
    </Box>
  );
};

export default RegisterSelection;
